import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  adminApi,
  extractErrorMessage,
  UF_OPTIONS,
  lookupCep,
  maskCep,
  onlyCepDigits,
} from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type {
  MerchantUserDto,
  MerchantAddressRequestDto,
  MerchantProfileDto,
  MerchantProfileRequestDto,
  MerchantDiscoveryOptionDto,
  ExternalLinkPlatformOptionDto,
  CategoryDto,
  ComboItem,
} from '@kardume/types';
import { EXTERNAL_LINK_ERROR_MESSAGES } from '@kardume/types';
import {
  PageHeader,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Button,
  TextInput,
  PhoneInput,
  DocumentInput,
  SearchableComboBox,
  RichTextInput,
  Checkbox,
  SegmentedControl,
  TagsInput,
  Tabs,
  TabPanel,
  ImageUpload,
  GalleryInput,
  ExternalLinksInput,
  externalLinksToRequest,
  externalLinksToPayload,
  validateExternalLinks,
  hasExternalLinkErrors,
} from '@kardume/ui';
import type { TabItem } from '@kardume/ui';
import DataState from '../components/DataState';
import FeaturedItemsPanel from '../components/featuredItems/FeaturedItemsPanel';
import { useAsync } from '../utils/useAsync';
import { uploadMerchantImage } from '../utils/imageUpload';

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const FullRow = styled.div`
  grid-column: 1 / -1;
`;

/**
 * Logomarca, capa e banner numa linha so, em molduras compactas.
 *
 * Antes os tres campos eram empilhados e cada moldura era desenhada na largura
 * disponivel: 1:1, 16:10 e 3:1 em largura de card dao tres alturas bem
 * diferentes, a capa vazia vira um retangulo enorme de nada e o Salvar (que
 * mora no rodape, fora das abas) sai da dobra. Escolher foto e tarefa de dois
 * cliques e estava ocupando a tela como se fosse a principal.
 *
 * No `compact` do `ImageUpload` a altura e comum (88px) e a largura sai da
 * proporcao: 88x88, 141x88 e 264x88. Alinhadas pela mesma altura, as larguras
 * diferentes e que mostram qual arte e quadrada e qual e larga.
 *
 * Abaixo de 900px viram duas colunas, e de 560px uma: a moldura de 264px do
 * banner mais o texto de ajuda nao cabem numa coluna de 1/3 de tela estreita.
 */
const ImagesRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.25rem;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

/**
 * Uma coluna da linha de imagens. A dica cresce para ocupar a sobra
 * (`ImageFieldHint`), e por isso as tres molduras comecam na mesma altura
 * mesmo quando uma dica ocupa duas linhas e a outra so uma - que era metade
 * do desalinhamento reclamado.
 */
const ImageField = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldGroupLabel = styled.h4`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
  margin: 0 0 0.5rem;
`;

const FieldGroupHint = styled.p`
  font-size: 0.78rem;
  color: ${(p) => p.theme.textMuted};
  margin: 0 0 0.6rem;
`;

/** Ver `ImageField`: e o `flex: 1` daqui que alinha as tres molduras. */
const ImageFieldHint = styled(FieldGroupHint)`
  /* 1 0 auto, e nao 1: a base e o proprio texto, entao a dica cresce para
     empurrar a moldura, mas nunca encolhe abaixo do que ela precisa. */
  flex: 1 0 auto;
`;

/**
 * Limite e formatos ficam uma vez so, embaixo da linha: repetidos sob cada
 * moldura compacta eles ocupavam mais espaco que a propria moldura.
 */
const ImagesNote = styled.p`
  margin: 0.9rem 0 0;
  font-size: 0.75rem;
  color: ${(p) => p.theme.textMuted};
`;

const FieldError = styled.span`
  display: block;
  margin-top: 0.35rem;
  font-size: 0.75rem;
  color: ${(p) => p.theme.error};
`;

/**
 * Rodapé do formulário: o Salvar vale para as três abas de uma vez, e por isso
 * vive fora delas - dentro de uma aba ele pareceria salvar só aquela.
 */
const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1.25rem;
`;

const FooterHint = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: ${(p) => p.theme.textMuted};
`;

const TabsBar = styled(Tabs)`
  margin-bottom: 1.25rem;
` as typeof Tabs;

const SectionSpacer = styled.div`
  height: 1.25rem;
`;

/**
 * Grade das caixas de marcar (comodidades, formas de pagamento).
 *
 * `auto-fill` com mínimo de 200px em vez de um número fixo de colunas: são doze
 * comodidades e sete formas de pagamento hoje, e cada valor novo do enum entra
 * sem ninguém precisar reajustar a contagem de colunas aqui.
 */
const ChoiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.6rem 1rem;
`;

/** Contador do "por que conhecer". Vira alerta ao passar do limite que a API recusa. */
const CharCount = styled.span<{ $over: boolean }>`
  display: block;
  margin-top: 0.3rem;
  font-size: 0.75rem;
  text-align: right;
  color: ${(p) => (p.$over ? p.theme.error : p.theme.textMuted)};
`;

const TextArea = styled.textarea<{ $error?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  min-height: 110px;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  font-family: inherit;
  resize: vertical;
  color: ${(p) => p.theme.textPrimary};
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => (p.$error ? p.theme.error : p.theme.border)};
  border-radius: ${(p) => p.theme.radius};

  &::placeholder {
    color: ${(p) => p.theme.textMuted};
  }
  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
    box-shadow: 0 0 0 3px ${(p) => p.theme.primary}22;
  }
`;

/**
 * Grade do endereço: três colunas em tela larga, uma em tela estreita.
 *
 * Própria, e não a `FormGrid` de duas colunas: bairro, cidade e UF são três
 * campos que se leem juntos, e quebrá-los em duas colunas põe a UF sozinha numa
 * linha, longe da cidade a que ela pertence.
 */
const AddressGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

/** Aviso sobre o mapa: informa, nunca impede o Salvar. */
const AddressNote = styled.p`
  margin: 0.9rem 0 0;
  font-size: 0.78rem;
  color: ${(p) => p.theme.textMuted};
`;

/** Os seis campos sem os quais o endereço não vira ponto no mapa nem rota. */
const REQUIRED_ADDRESS_FIELDS: (keyof MerchantAddressRequestDto)[] = [
  'zipCode',
  'street',
  'number',
  'district',
  'city',
  'state',
];

/** Limite do "por que conhecer", igual ao `@Size(max = 600)` do core. */
const WHY_VISIT_MAX = 600;

/** Valor do seletor de faixa quando o lojista não declarou nada. */
const NO_PRICE_RANGE = '';

/** Tetos das palavras-chave, iguais aos do `MerchantKeywords` do core (#297). */
const KEYWORDS_MAX = 15;
const KEYWORD_MAX_LENGTH = 40;
const KEYWORD_MIN_LENGTH = 2;

// ---------------------------------------------------------------------------
// Abas (card 294)
//
// O corte é pela pergunta que o lojista traz - "como minha loja aparece",
// "meus dados", "como me encontram" - e não pelo tipo do campo. Por isso a
// descrição e a categoria moram em Descoberta, e não junto do CNPJ: as duas
// respondem por que alguém escolhe esta loja, e é isso que alimenta a busca.
// ---------------------------------------------------------------------------

type ProfileTabId = 'images' | 'data' | 'discovery';

const PROFILE_TAB_ORDER: ProfileTabId[] = ['images', 'data', 'discovery'];

const PROFILE_TAB_LABELS: Record<ProfileTabId, string> = {
  images: 'Imagens',
  data: 'Dados da loja',
  discovery: 'Descoberta',
};

type ProfileField = keyof MerchantProfileRequestDto;

/** A que aba pertence cada campo do payload. */
const TAB_FIELDS = {
  images: ['logoUrl', 'coverUrl', 'bannerUrl', 'galleryUrls'],
  data: [
    'name',
    'tradeName',
    'document',
    'email',
    'phone',
    'whatsapp',
    'instagram',
    'website',
    'address',
    'externalLinks',
  ],
  discovery: [
    'categoryId',
    'description',
    'whyVisit',
    'priceRange',
    'amenities',
    'paymentMethods',
    'keywords',
  ],
} as const;

type MappedProfileField = (typeof TAB_FIELDS)[ProfileTabId][number];

/**
 * Quebra a compilação se um campo novo entrar em `MerchantProfileRequestDto` e
 * ninguém disser em que aba ele mora. Campo órfão não aparece em tela nenhuma -
 * e o `Salvar` continuaria mandando o valor antigo, calado.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const EVERY_FIELD_HAS_A_TAB: Record<Exclude<ProfileField, MappedProfileField>, never> = {};

type ProfileFieldErrors = Partial<Record<ProfileField, string>>;

/** Suficiente para pegar o erro de digitação; quem valida de verdade é o core. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validação do formulário inteiro, aba escondida incluída.
 *
 * Vive aqui, e não no `required` do navegador, justamente por causa das abas: o
 * campo obrigatório de uma aba fechada não está no DOM, o navegador não o vê e
 * o `submit` passaria batido - ou, pior, com o campo montado o navegador
 * bloquearia o envio com um balão que ninguém consegue estilizar nem apontar.
 * O `<form>` vai com `noValidate` por isso.
 */
function validateProfile(
  form: MerchantProfileRequestDto,
  platforms: ExternalLinkPlatformOptionDto[]
): ProfileFieldErrors {
  const errors: ProfileFieldErrors = {};

  if (!form.name.trim()) errors.name = 'Informe a razão social.';
  if (!form.tradeName.trim()) errors.tradeName = 'Informe o nome fantasia.';
  if (!form.document.trim()) errors.document = 'Informe o CNPJ ou o CPF.';
  if (!form.email.trim()) errors.email = 'Informe o e-mail de contato.';
  else if (!EMAIL_PATTERN.test(form.email.trim())) errors.email = 'E-mail inválido.';
  if (!form.phone.trim()) errors.phone = 'Informe o telefone.';
  if (!form.categoryId) errors.categoryId = 'Selecione a categoria da loja.';

  // O core recusa acima de 600 com um 400 genérico; avisar aqui poupa a viagem
  // e diz qual campo está errado.
  if ((form.whyVisit ?? '').trim().length > WHY_VISIT_MAX) {
    errors.whyVisit = `Use no máximo ${WHY_VISIT_MAX} caracteres.`;
  }

  /*
    Endereço: valida FORMATO, não presença - a mesma régua do core.

    Exigir os seis campos aqui recusaria o Salvar de uma loja que entrou pelo
    guia com endereço pela metade e só queria trocar a foto de capa (card #241:
    o cadastro não trava por falta de um dado). O que falta aparece como aviso
    logo abaixo do bloco e no checklist de onboarding, que é onde essa cobrança
    mora. O que é recusado é o que erra calado: CEP pela metade não geocodifica
    e ninguém descobre por quê.
  */
  const zip = onlyCepDigits(form.address.zipCode ?? '');
  if (zip.length > 0 && zip.length !== 8) errors.address = 'CEP deve ter 8 dígitos.';

  /*
    Palavras-chave: os mesmos tetos do core, avisados aqui para o lojista não
    descobrir o limite por um 400 depois de digitar quinze etiquetas. O core
    continua sendo quem valida de verdade - e é ele que normaliza acento, caixa
    e duplicata, então a tela não tenta adivinhar isso.
  */
  const longKeyword = form.keywords.find((k) => k.trim().length > KEYWORD_MAX_LENGTH);
  const shortKeyword = form.keywords.find((k) => k.trim().length < KEYWORD_MIN_LENGTH);
  if (form.keywords.length > KEYWORDS_MAX) {
    errors.keywords = `Use no máximo ${KEYWORDS_MAX} palavras-chave.`;
  } else if (longKeyword) {
    errors.keywords = `"${longKeyword}" passa de ${KEYWORD_MAX_LENGTH} caracteres - palavra-chave não é frase.`;
  } else if (shortKeyword) {
    errors.keywords = `"${shortKeyword}" é curta demais: use pelo menos ${KEYWORD_MIN_LENGTH} caracteres.`;
  }

  if (hasExternalLinkErrors(validateExternalLinks(form.externalLinks, platforms))) {
    errors.externalLinks = 'Revise os canais de venda: há linha incompleta ou endereço inválido.';
  }

  return errors;
}

/** Primeira aba (na ordem em que aparecem) que tem campo a corrigir. */
const firstTabWithError = (errors: ProfileFieldErrors): ProfileTabId | undefined =>
  PROFILE_TAB_ORDER.find((tab) => TAB_FIELDS[tab].some((field) => !!errors[field]));

/** Só os campos de uma aba, na ordem fixa do mapa - para comparar com o salvo. */
const sliceOfTab = (form: MerchantProfileRequestDto, tab: ProfileTabId): unknown[] =>
  TAB_FIELDS[tab].map((field) => form[field]);

interface ProfilePageProps {
  user: MerchantUserDto;
  onUserUpdated: (user: MerchantUserDto) => void;
}

/** Build the editable request payload from the loaded profile (no status). */
function toRequest(profile: MerchantProfileDto): MerchantProfileRequestDto {
  return {
    name: profile.name,
    tradeName: profile.tradeName,
    document: profile.document,
    categoryId: profile.categoryId,
    email: profile.email,
    phone: profile.phone,
    whatsapp: profile.whatsapp ?? '',
    website: profile.website ?? '',
    instagram: profile.instagram ?? '',
    description: profile.description ?? '',
    logoUrl: profile.logoUrl,
    coverUrl: profile.coverUrl,
    bannerUrl: profile.bannerUrl,
    galleryUrls: profile.galleryUrls ?? [],
    // Sem as coordenadas: elas vêm na resposta só para a tela poder dizer se a
    // loja já tem lugar no mapa, e o PUT não as aceita de volta.
    address: {
      zipCode: profile.address?.zipCode ?? '',
      street: profile.address?.street ?? '',
      number: profile.address?.number ?? '',
      complement: profile.address?.complement ?? '',
      district: profile.address?.district ?? '',
      city: profile.address?.city ?? '',
      state: profile.address?.state ?? '',
    },
    externalLinks: externalLinksToRequest(profile.externalLinks),
    whyVisit: profile.whyVisit ?? '',
    // `?? null` e não `?? 1`: faixa não declarada tem que continuar não
    // declarada. Uma faixa chutada é pior que faixa nenhuma - o visitante
    // decide por ela se vai ou não ao lugar.
    priceRange: profile.priceRange ?? null,
    amenities: profile.amenities ?? [],
    paymentMethods: profile.paymentMethods ?? [],
    keywords: profile.keywords ?? [],
  };
}

const ProfilePage: React.FC<ProfilePageProps> = ({ user, onUserUpdated }) => {
  const { showNotification } = useNotification();

  const load = useCallback(
    () =>
      Promise.all([
        adminApi.getProfile(),
        adminApi.listCategories(),
        adminApi.listExternalLinkPlatforms(),
        adminApi.getDiscoveryOptions(),
      ]).then(([profile, categories, externalLinkPlatforms, discoveryOptions]) => ({
        profile,
        categories,
        externalLinkPlatforms,
        discoveryOptions,
      })),
    []
  );
  const { data, loading, error, reload } = useAsync(load, []);

  const [form, setForm] = useState<MerchantProfileRequestDto | null>(null);
  /**
   * Último estado gravado. É o espelho contra o qual cada aba descobre se tem
   * rascunho - sem ele, "alterado" só existiria durante o clique.
   */
  const [saved, setSaved] = useState<MerchantProfileRequestDto | null>(null);
  const [activeTab, setActiveTab] = useState<ProfileTabId>('images');
  /**
   * Erro só aparece depois da primeira tentativa de salvar. Pintar de vermelho
   * um formulário recém-aberto é acusar o lojista de algo que ele ainda não fez.
   */
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  /**
   * Coordenada que o core tem hoje para esta loja. Fica fora do `form` porque
   * ela não é editável aqui - é resposta, não pergunta -, e vem do último
   * `GET`/`PUT`: depois de salvar um CEP novo o core regeocodifica, e é este
   * valor que diz se ele conseguiu.
   */
  const [mapPoint, setMapPoint] = useState<{ latitude?: number; longitude?: number }>({});

  useEffect(() => {
    if (data?.profile) {
      const request = toRequest(data.profile);
      setForm(request);
      setSaved(request);
      setMapPoint({
        latitude: data.profile.address?.latitude,
        longitude: data.profile.address?.longitude,
      });
      setShowErrors(false);
    }
  }, [data]);

  const categoryItems: ComboItem[] = useMemo(
    () => (data?.categories ?? []).map((c: CategoryDto) => ({ id: c.id, label: c.name })),
    [data]
  );

  const externalLinkPlatforms = useMemo(() => data?.externalLinkPlatforms ?? [], [data]);
  const discovery = data?.discoveryOptions;

  const errors = useMemo(
    () => (form ? validateProfile(form, externalLinkPlatforms) : {}),
    [form, externalLinkPlatforms]
  );

  /** Abas com rascunho não salvo, para a marca da barra e a dica do rodapé. */
  const draftTabs = useMemo(() => {
    if (!form || !saved) return new Set<ProfileTabId>();
    return new Set(
      PROFILE_TAB_ORDER.filter(
        (tab) => JSON.stringify(sliceOfTab(form, tab)) !== JSON.stringify(sliceOfTab(saved, tab))
      )
    );
  }, [form, saved]);

  const tabs: TabItem<ProfileTabId>[] = useMemo(
    () =>
      PROFILE_TAB_ORDER.map((tab) => {
        const broken = showErrors && TAB_FIELDS[tab].some((field) => !!errors[field]);
        return {
          value: tab,
          label: PROFILE_TAB_LABELS[tab],
          // Erro ganha da marca de rascunho: quem tem as duas precisa ver a que
          // impede o Salvar, não a que só informa.
          status: broken ? 'error' : draftTabs.has(tab) ? 'draft' : undefined,
        };
      }),
    [showErrors, errors, draftTabs]
  );

  /** Erro do campo, só depois da primeira tentativa de salvar. */
  const errorOf = (field: ProfileField): string | undefined => (showErrors ? errors[field] : undefined);

  /**
   * Marca/desmarca um valor de um conjunto (comodidades, formas de pagamento).
   *
   * A ordem gravada segue a do catálogo, e não a de clique: o core devolve as
   * tags na ordem do enum, e se a tela enviasse a ordem de clique o formulário
   * ficaria "sujo" logo depois de salvar, só porque a lista voltou reordenada.
   */
  const toggleFromCatalog = (
    key: 'amenities' | 'paymentMethods',
    catalog: MerchantDiscoveryOptionDto[],
    value: string
  ) => {
    setForm((prev) => {
      if (!prev) return prev;
      const current = new Set(prev[key]);
      if (current.has(value)) current.delete(value);
      else current.add(value);
      return { ...prev, [key]: catalog.map((o) => o.value).filter((v) => current.has(v)) };
    });
  };

  const update = <K extends keyof MerchantProfileRequestDto>(key: K, value: MerchantProfileRequestDto[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const updateAddress = <K extends keyof MerchantAddressRequestDto>(
    key: K,
    value: MerchantAddressRequestDto[K]
  ) => {
    setForm((prev) => (prev ? { ...prev, address: { ...prev.address, [key]: value } } : prev));
  };

  /**
   * Busca o CEP e preenche o resto do endereço.
   *
   * Mesma consulta que as duas telas do backoffice usam (`@kardume/services`),
   * e pelo mesmo motivo: três cópias do ViaCEP discordariam no dia em que o
   * provedor mudar. Só sobrescreve o que o provedor soube responder - quem
   * digitou o bairro à mão não o perde porque o CEP genérico da cidade não traz
   * bairro.
   *
   * Falha de rede e CEP inexistente não viram aviso: digitar `1234` a caminho
   * de um CEP válido não é engano do lojista, e o campo continua editável.
   */
  const handleCep = async (raw: string): Promise<void> => {
    const cep = onlyCepDigits(raw);
    updateAddress('zipCode', cep);
    if (cep.length !== 8) return;
    setCepLoading(true);
    const found = await lookupCep(cep);
    setCepLoading(false);
    if (!found) return;
    setForm((prev) =>
      prev
        ? {
            ...prev,
            address: {
              ...prev.address,
              street: found.street || prev.address.street,
              district: found.district || prev.address.district,
              city: found.city || prev.address.city,
              state: found.state || prev.address.state,
            },
          }
        : prev
    );
  };

  /** O que ainda falta para o endereço virar ponto no mapa. Avisa, não bloqueia. */
  const missingAddressFields = form
    ? REQUIRED_ADDRESS_FIELDS.filter((field) => !(form.address[field] ?? '').trim())
    : [];

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form || saving) return;

    // Levar a pessoa até o erro é o ponto: o formulário é um só, mas o campo
    // recusado pode estar numa aba fechada - e aí o botão "não faz nada".
    setShowErrors(true);
    const broken = firstTabWithError(validateProfile(form, externalLinkPlatforms));
    if (broken) {
      setActiveTab(broken);
      showNotification(
        broken === activeTab
          ? 'Revise os campos destacados antes de salvar.'
          : `Há campos a corrigir em "${PROFILE_TAB_LABELS[broken]}" - abrimos a aba para você.`,
        'error'
      );
      return;
    }

    setSaving(true);
    try {
      // Blank rows never reach the core: the whole list replaces the current set,
      // so an unfinished row would either be refused or stored half-filled.
      const updated = await adminApi.updateProfile({
        ...form,
        externalLinks: externalLinksToPayload(form.externalLinks),
        // Textarea esvaziado chega como "" ou como espaços. O core já trata
        // isso como nulo; mandar o vazio explícito evita depender disso para
        // que o bloco "Por que conhecer" suma da página pública.
        whyVisit: (form.whyVisit ?? '').trim() || undefined,
      });
      const request = toRequest(updated);
      setForm(request);
      setSaved(request);
      setMapPoint({ latitude: updated.address?.latitude, longitude: updated.address?.longitude });
      setShowErrors(false);
      onUserUpdated({
        ...user,
        merchant: {
          ...user.merchant,
          name: updated.name,
          tradeName: updated.tradeName,
          categoryId: updated.categoryId,
          email: updated.email,
          phone: updated.phone,
          description: updated.description,
          planId: updated.planId,
        },
      });
      showNotification('Perfil atualizado com sucesso.', 'success');
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível salvar o perfil.', EXTERNAL_LINK_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const draftHint =
    draftTabs.size === 0
      ? 'Abas são navegação, não formulários separados: o Salvar grava as três de uma vez.'
      : `Alterações não salvas em ${PROFILE_TAB_ORDER.filter((tab) => draftTabs.has(tab))
          .map((tab) => PROFILE_TAB_LABELS[tab])
          .join(', ')}.`;

  return (
    <>
      <PageHeader
        title="Perfil da loja"
        subtitle="Gerencie as informações públicas e cadastrais do seu estabelecimento."
        actions={
          // Card 226 - o parceiro vê aqui o mesmo selo que o cliente vê na
          // vitrine. Só de leitura: quem acende e apaga é o Kardume, com
          // motivo registrado. Loja sem parceria não recebe rótulo nenhum,
          // nem um "não parceira" - a ausência já é a informação (§37.5).
          data?.profile?.partner ? <Badge tone="success">✓ Parceiro Kardume</Badge> : undefined
        }
      />

      <DataState loading={loading} error={error} onRetry={reload}>
        {form && (
          <form onSubmit={handleSubmit} noValidate>
            <TabsBar
              value={activeTab}
              onChange={setActiveTab}
              tabs={tabs}
              ariaLabel="Seções do perfil da loja"
              idPrefix="profile"
            />

            {activeTab === 'images' && (
              <TabPanel tab="images" idPrefix="profile">
                <Card>
                  <CardHeader>
                    <h3>Imagens</h3>
                  </CardHeader>
                  <CardBody>
                    {/*
                      Capa e banner são dois campos porque as duas artes são lidas
                      em tamanhos opostos: a capa vive num cartão de ~200px numa
                      grade e o banner ocupa a largura toda atrás do nome da loja.
                      Sem a dica logo abaixo do rótulo, o lojista sobe a mesma
                      imagem nos dois e a separação não serve para nada.

                      A ordem é da moldura mais estreita para a mais larga
                      (1:1 → 16:10 → 3:1), e é a mesma do passo Marca do
                      backoffice: com a altura comum, a linha inteira vira uma
                      escala de proporções lida de uma olhada.

                      A proporção saiu da dica e virou o texto de ajuda do
                      próprio campo, abaixo da moldura - mesma posição nos três,
                      e é lá que o erro de upload também aparece.

                      Os três campos usavam o `ImageUploader` e passaram ao
                      `ImageUpload` no card #301, que acabou com a duplicata no
                      design system (o porquê da direção está no KDoc dele). A
                      proporção agora vem da `variant` em vez de uma string, e
                      o envio é do próprio componente - era o `handleImageField`
                      daqui que remontava esse contrato à mão.
                    */}
                    <ImagesRow>
                      <ImageField>
                        <FieldGroupLabel>Logomarca</FieldGroupLabel>
                        <ImageFieldHint>
                          Identifica sua loja nas listagens e no topo da página.
                        </ImageFieldHint>
                        <ImageUpload
                          variant="logo"
                          compact
                          value={form.logoUrl || undefined}
                          onChange={(url) => update('logoUrl', url)}
                          onUpload={uploadMerchantImage('LOGO')}
                          onError={(message) => showNotification(message, 'error')}
                          helpText="Formato 1:1."
                        />
                      </ImageField>
                      <ImageField>
                        <FieldGroupLabel>Capa</FieldGroupLabel>
                        <ImageFieldHint>
                          Aparece nos cartões da vitrine e da busca.
                        </ImageFieldHint>
                        {/*
                          Mesma capa que o backoffice edita, então mesma
                          proporção (card #302): 16:10, que é como o cartão da
                          vitrine e da busca a recorta no site. Dizia 16:9 aqui
                          e 16:10 lá - duas telas pedindo arte diferente para o
                          mesmo slot.
                        */}
                        <ImageUpload
                          variant="merchantCover"
                          compact
                          value={form.coverUrl || undefined}
                          onChange={(url) => update('coverUrl', url)}
                          onUpload={uploadMerchantImage('COVER')}
                          onError={(message) => showNotification(message, 'error')}
                          helpText="Formato 16:10 (1280×800)."
                        />
                      </ImageField>
                      <ImageField>
                        <FieldGroupLabel>Banner</FieldGroupLabel>
                        <ImageFieldHint>
                          Fundo da página da sua loja, atrás do nome.
                        </ImageFieldHint>
                        <ImageUpload
                          variant="banner"
                          compact
                          value={form.bannerUrl || undefined}
                          onChange={(url) => update('bannerUrl', url)}
                          onUpload={uploadMerchantImage('BANNER')}
                          onError={(message) => showNotification(message, 'error')}
                          helpText="Formato 3:1 (1920×640)."
                        />
                      </ImageField>
                    </ImagesRow>
                    <ImagesNote>
                      Tamanho máximo: 5 MB por imagem. Formatos aceitos: JPG, PNG e GIF.
                    </ImagesNote>
                    <SectionSpacer />
                    <GalleryInput
                      label="Galeria de imagens"
                      value={form.galleryUrls}
                      onChange={(urls) => update('galleryUrls', urls)}
                      onUpload={uploadMerchantImage('GALLERY')}
                      onError={(msg) => showNotification(msg, 'error')}
                      helpText="Adicione fotos do estabelecimento (até 12)."
                      max={12}
                    />
                  </CardBody>
                </Card>
              </TabPanel>
            )}

            {activeTab === 'data' && (
              <TabPanel tab="data" idPrefix="profile">
                <Card>
                  <CardHeader>
                    <h3>Dados da loja</h3>
                  </CardHeader>
                  <CardBody>
                    <FormGrid>
                      <TextInput
                        label="Razão social"
                        value={form.name}
                        onChange={(e) => update('name', e.target.value)}
                        error={errorOf('name')}
                        required
                      />
                      <TextInput
                        label="Nome fantasia"
                        value={form.tradeName}
                        onChange={(e) => update('tradeName', e.target.value)}
                        error={errorOf('tradeName')}
                        required
                      />
                      <DocumentInput
                        label="CNPJ / CPF"
                        value={form.document}
                        onChange={(v) => update('document', v)}
                        error={errorOf('document')}
                        required
                      />
                      <TextInput
                        label="E-mail"
                        type="email"
                        value={form.email}
                        onChange={(e) => update('email', e.target.value)}
                        error={errorOf('email')}
                        required
                      />
                      <PhoneInput
                        label="Telefone"
                        value={form.phone}
                        onChange={(v) => update('phone', v)}
                        error={errorOf('phone')}
                        required
                      />
                      <PhoneInput
                        label="WhatsApp"
                        value={form.whatsapp ?? ''}
                        onChange={(v) => update('whatsapp', v)}
                      />
                      <TextInput
                        label="Instagram"
                        value={form.instagram ?? ''}
                        onChange={(e) => update('instagram', e.target.value)}
                        placeholder="@sualoja"
                      />
                      <TextInput
                        label="Site"
                        value={form.website ?? ''}
                        onChange={(e) => update('website', e.target.value)}
                        placeholder="https://"
                      />
                      <FullRow>
                        <FieldGroupLabel>Endereço</FieldGroupLabel>
                        <FieldGroupHint>
                          É por aqui que o cliente traça a rota até a sua loja. Digite o CEP e o resto
                          preenche sozinho; confira o número e o complemento.
                        </FieldGroupHint>
                        <AddressGrid>
                          <TextInput
                            label={cepLoading ? 'CEP (buscando...)' : 'CEP'}
                            value={maskCep(form.address.zipCode ?? '')}
                            onChange={(e) => handleCep(e.target.value)}
                            placeholder="00000-000"
                            error={errorOf('address')}
                          />
                          <TextInput
                            label="Rua"
                            value={form.address.street ?? ''}
                            onChange={(e) => updateAddress('street', e.target.value)}
                            containerStyle={{ gridColumn: 'span 2' }}
                          />
                          <TextInput
                            label="Número"
                            value={form.address.number ?? ''}
                            onChange={(e) => updateAddress('number', e.target.value)}
                          />
                          <TextInput
                            label="Complemento"
                            value={form.address.complement ?? ''}
                            onChange={(e) => updateAddress('complement', e.target.value)}
                            placeholder="Ex.: Sala 101"
                            containerStyle={{ gridColumn: 'span 2' }}
                          />
                          <TextInput
                            label="Bairro"
                            value={form.address.district ?? ''}
                            onChange={(e) => updateAddress('district', e.target.value)}
                          />
                          <TextInput
                            label="Cidade"
                            value={form.address.city ?? ''}
                            onChange={(e) => updateAddress('city', e.target.value)}
                          />
                          <SearchableComboBox
                            label="Estado (UF)"
                            items={UF_OPTIONS}
                            valueId={form.address.state || undefined}
                            onChange={(id) => updateAddress('state', id)}
                            placeholder="UF"
                          />
                        </AddressGrid>
                        {/*
                          O ponto no mapa é consequência do endereço, nunca um
                          campo: o painel não tem mapa e o lojista não digita
                          coordenada. O que a tela deve é dizer em que pé está,
                          porque "some do mapa" é justamente o efeito que
                          ninguém relaciona com o CEP que acabou de trocar.
                        */}
                        {missingAddressFields.length > 0 ? (
                          <AddressNote>
                            Complete o endereço para a sua loja aparecer no mapa e nas buscas por
                            proximidade. Ela continua na vitrine enquanto isso.
                          </AddressNote>
                        ) : mapPoint.latitude == null ? (
                          <AddressNote>
                            Ainda não conseguimos localizar este endereço no mapa. A loja continua na
                            vitrine, mas fica de fora dos filtros por distância - confira o CEP e
                            salve de novo.
                          </AddressNote>
                        ) : (
                          <AddressNote>
                            Localização no mapa em dia. Ao trocar o CEP, a cidade ou o estado, ela é
                            recalculada quando você salvar.
                          </AddressNote>
                        )}
                      </FullRow>
                      <FullRow>
                        <FieldGroupLabel>Canais de venda</FieldGroupLabel>
                        <FieldGroupHint>
                          Divulgue no seu perfil os links das suas lojas em marketplaces, apps de delivery e
                          redes sociais. A ordem definida aqui é a ordem em que eles aparecem.
                        </FieldGroupHint>
                        <ExternalLinksInput
                          value={form.externalLinks}
                          onChange={(links) => update('externalLinks', links)}
                          platforms={externalLinkPlatforms}
                        />
                        {errorOf('externalLinks') && <FieldError>{errorOf('externalLinks')}</FieldError>}
                      </FullRow>
                    </FormGrid>
                  </CardBody>
                </Card>

                {/*
                  A vitrine mora aqui, e não numa aba própria, porque a pergunta
                  que ela responde é a mesma de "Dados da loja": o que esta loja
                  é e o que ela oferece. Aba própria para um recurso de um card
                  só transformaria a barra num menu de funcionalidades, que é o
                  corte que o #294 recusou.

                  Card separado, e não mais um bloco no formulário acima, porque
                  ele não é do formulário: tem CRUD próprio e grava na hora (ver
                  o comentário do `FeaturedItemsPanel`). A moldura separada é o
                  que faz a diferença ser visível antes de o lojista procurar o
                  Salvar que não existe ali dentro.
                */}
                <SectionSpacer />
                <FeaturedItemsPanel />
              </TabPanel>
            )}

            {/*
              Descoberta não é "o resto": é o que faz alguém escolher este lugar
              e não o da esquina, e é o mesmo dado que alimenta os filtros do
              catálogo. Junto do CNPJ e do telefone, o lojista lê tudo como
              burocracia e deixa em branco justamente o que vende - por isso a
              categoria e a descrição vieram para cá.

              O bloco de vocabulário (faixa, comodidades, pagamentos) só existe
              se o catálogo carregou: seletor sem vocabulário seria uma moldura
              vazia. Categoria, descrição e "por que conhecer" não dependem dele
              e continuam editáveis mesmo se aquela chamada falhar.
            */}
            {activeTab === 'discovery' && (
              <TabPanel tab="discovery" idPrefix="profile">
                <Card>
                  <CardHeader>
                    <h3>Descoberta</h3>
                  </CardHeader>
                  <CardBody>
                    <FormGrid>
                      <FullRow>
                        <SearchableComboBox
                          label="Categoria"
                          items={categoryItems}
                          valueId={form.categoryId}
                          onChange={(id) => update('categoryId', id)}
                          placeholder="Selecione a categoria"
                          error={errorOf('categoryId')}
                          required
                        />
                      </FullRow>

                      <FullRow>
                        <RichTextInput
                          label="Descrição"
                          value={form.description ?? ''}
                          onChange={(html) => update('description', html)}
                          placeholder="Conte um pouco sobre a sua loja para os clientes."
                        />
                      </FullRow>

                      <FullRow>
                        <FieldGroupLabel>Por que conhecer</FieldGroupLabel>
                        <FieldGroupHint>
                          Duas ou três linhas sobre o que a pessoa vai encontrar aí e não encontra em
                          outro lugar. É diferente da descrição: aquela conta o que você vende, esta é o
                          motivo para atravessar a cidade.
                        </FieldGroupHint>
                        <TextArea
                          $error={!!errorOf('whyVisit')}
                          value={form.whyVisit ?? ''}
                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            update('whyVisit', e.target.value)
                          }
                          placeholder="Ex.: forno a lenha aceso desde 1998 e a fila da esquina que se forma às seis."
                        />
                        <CharCount $over={(form.whyVisit ?? '').length > WHY_VISIT_MAX}>
                          {(form.whyVisit ?? '').length} / {WHY_VISIT_MAX}
                        </CharCount>
                      </FullRow>

                      <FullRow>
                        {/*
                          Texto livre, e não catálogo: o que a palavra-chave tem
                          de útil é ser específica desta loja - "marmita",
                          "conserto de bike" -, e isso um catálogo comum a todo
                          o guia não entrega. Por isso ela também não depende do
                          `discovery` carregar, ao contrário dos blocos abaixo.

                          Ela não aparece na página da loja: alimenta busca. É o
                          que mantém a §37 fora de risco (nada aqui pode virar
                          selo ou juízo de qualidade) e o que faz um termo
                          bravateiro custar só uma busca pior, nunca um texto
                          publicado.
                        */}
                        <FieldGroupLabel>Palavras-chave</FieldGroupLabel>
                        <FieldGroupHint>
                          Os termos que as pessoas digitam para achar o que você faz e que não cabem em
                          categoria nem em comodidade - &quot;marmita&quot;, &quot;sem glúten&quot;,
                          &quot;conserto de bike&quot;, &quot;aniversário infantil&quot;. Elas ajudam a
                          busca a encontrar você; não aparecem na sua página.
                        </FieldGroupHint>
                        <TagsInput
                          value={form.keywords}
                          onChange={(keywords) => update('keywords', keywords)}
                          placeholder="Digite um termo e pressione Enter..."
                          error={errorOf('keywords')}
                          helpText={`${form.keywords.length} de ${KEYWORDS_MAX} · até ${KEYWORD_MAX_LENGTH} caracteres cada. Acento e maiúscula não mudam nada na busca.`}
                        />
                      </FullRow>

                      {discovery && (
                        <FullRow>
                          <FieldGroupLabel>Faixa de preço</FieldGroupLabel>
                          <FieldGroupHint>
                            Quanto a pessoa gasta numa visita comum. Se preferir não declarar, deixe em
                            &quot;Não informar&quot; - é melhor não mostrar nada do que mostrar uma faixa
                            que não corresponde.
                          </FieldGroupHint>
                          <SegmentedControl
                            value={form.priceRange == null ? NO_PRICE_RANGE : String(form.priceRange)}
                            onChange={(value) =>
                              update('priceRange', value === NO_PRICE_RANGE ? null : Number(value))
                            }
                            options={[
                              { value: NO_PRICE_RANGE, label: 'Não informar' },
                              ...discovery.priceRanges.map((range) => ({
                                value: String(range.value),
                                label: `${range.symbol} · ${range.label}`,
                              })),
                            ]}
                          />
                        </FullRow>
                      )}

                      {discovery && (
                        <FullRow>
                          <FieldGroupLabel>O que você encontra aqui</FieldGroupLabel>
                          <FieldGroupHint>
                            Marque só o que a loja realmente oferece. Isto vira etiqueta na sua página e
                            filtro na busca - quem procura &quot;pet friendly&quot; encontra você por
                            aqui.
                          </FieldGroupHint>
                          <ChoiceGrid>
                            {discovery.amenities.map((amenity) => (
                              <Checkbox
                                key={amenity.value}
                                label={amenity.label}
                                checked={form.amenities.includes(amenity.value)}
                                onChange={() =>
                                  toggleFromCatalog('amenities', discovery.amenities, amenity.value)
                                }
                              />
                            ))}
                          </ChoiceGrid>
                        </FullRow>
                      )}

                      {discovery && (
                        <FullRow>
                          <FieldGroupLabel>Formas de pagamento</FieldGroupLabel>
                          <FieldGroupHint>
                            O que você aceita no balcão. Aparece na página da loja para a pessoa não
                            descobrir só na hora de pagar.
                          </FieldGroupHint>
                          <ChoiceGrid>
                            {discovery.paymentMethods.map((method) => (
                              <Checkbox
                                key={method.value}
                                label={method.label}
                                checked={form.paymentMethods.includes(method.value)}
                                onChange={() =>
                                  toggleFromCatalog(
                                    'paymentMethods',
                                    discovery.paymentMethods,
                                    method.value
                                  )
                                }
                              />
                            ))}
                          </ChoiceGrid>
                        </FullRow>
                      )}
                    </FormGrid>
                  </CardBody>
                </Card>
              </TabPanel>
            )}

            {/* Fora das abas: o botão fecha o formulário inteiro, e não a aba
                aberta. Dentro de uma delas, o lojista leria "salvar esta aba" -
                e o que grava é sempre a loja toda. */}
            <Footer>
              <FooterHint>{draftHint}</FooterHint>
              <Button type="submit" variant="primary" isLoading={saving}>
                Salvar alterações
              </Button>
            </Footer>
          </form>
        )}
      </DataState>
    </>
  );
};

export default ProfilePage;
